import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppService } from 'src/app/app.service';
import { UserAssetsService } from 'src/app/pages/user-asset/user-assets.service';
import { BonusApi } from 'src/app/shared/apis/bonus.api';
import { CSSelectComponent } from 'src/app/shared/components/cs-select/cs-select.component';
import { CustomizeFormModule } from 'src/app/shared/components/customize-form/customize-form.module';
import { SavedIntentBonusData, SelectedBonusData } from 'src/app/shared/interfaces/deposit.interface';
import { PipesModule } from 'src/app/shared/pipes/pipes.module';
import { LocalStorageService } from 'src/app/shared/service/localstorage.service';
import { WalletService } from './../../wallet.service';
import { DepositBonusDropdownService } from './deposit-bonus-dropdown.service';

type BonusSelectionFlow = 'legacyImmediate' | 'transferIntent';

@Component({
  selector: 'app-deposit-bonus-dropdown',
  standalone: true,
  imports: [CustomizeFormModule, PipesModule, CSSelectComponent],
  templateUrl: './deposit-bonus-dropdown.component.html',
  styleUrl: './deposit-bonus-dropdown.component.scss',
})
export class DepositBonusDropdownComponent implements OnInit, OnChanges, OnDestroy {
  constructor(
    private depositBonusDropdownService: DepositBonusDropdownService,
    private bonusApi: BonusApi,
    private walletService: WalletService,
    private userAssetsService: UserAssetsService,
    private route: ActivatedRoute,
    private appService: AppService,
    private localStorageService: LocalStorageService,
  ) {}

  /** bonus variable */
  // 當前選擇的紅利 雙向綁定
  selectedBonus = '';
  // 紅利的最低存款金額與返回的倍率
  rateVos: { minDepositUsdt: number; rate: number; minDeposit: number } | null = null;
  // 這邊用any[]沒用到BonusList[]，是因為這邊要符合cs-select的資料格式
  bonusList: any[] = [];
  // 紅利加載狀態
  bonusLoading: boolean = false;
  // 是否第一次載入，因為ngOnChanges會在ngOnInit之前執行
  isFirst: boolean = true;

  // 所有匯率base USDT
  allRateBaseUsdt: { currency: string; rate: number }[] = [];

  @Input() currentCurrency: string = 'USDT';
  @Input() bonusSelectionFlow: BonusSelectionFlow = 'legacyImmediate';
  @Input() revertBonusSelection?: {
    activityNo: string;
    version: number;
  };

  @Output() bonusLoadingChange = new EventEmitter<boolean>();
  @Output() activityNo = new EventEmitter<string>();
  @Output() bonusSelectionChange = new EventEmitter<SelectedBonusData>();
  @Output() savedIntentBonusChange = new EventEmitter<SavedIntentBonusData>();

  bonusId: string = '';

  ngOnInit(): void {
    // 如果有暫存的bonus資料，就直接使用暫存資料
    // 否則就重新獲取bonus資料
    const preservedBonusState = this.depositBonusDropdownService.preservedBonusState;
    if (preservedBonusState && this.bonusSelectionFlow === 'legacyImmediate') {
      this.selectedBonus = preservedBonusState.selectedBonus;
      this.bonusList = preservedBonusState.bonusList;
      this.rateVos = this.bonusList.find(x => x.bonusActivitiesNo === this.selectedBonus)?.rateVos[0] || null;
    } else if (preservedBonusState) {
      // Transfer intent flow 必須重新取得最新的 isCurrentCryptoDepositBonus。
      this.checkBonus(true);
    } else {
      (async () => {
        const params = await new Promise<any>(resolve => {
          this.route.queryParams.subscribe(resolve);
        });
        this.bonusId = params['bonusId'] || '';
        this.checkBonus(true);
      })();
    }
    this.isFirst = false;
    // 獲取匯率用於minDeposit顯示
    this.userAssetsService.allRate.subscribe(data => {
      this.allRateBaseUsdt = data.rates;
    });
  }
  ngOnChanges(changes: SimpleChanges): void {
    // 取消更新 intent 時，將 dropdown 還原成後端目前保存的 bonus。
    if (changes['revertBonusSelection'] && !changes['revertBonusSelection'].firstChange) {
      this.selectedBonus = this.revertBonusSelection?.activityNo || '';
      this.updateBonusSelection(this.revertBonusSelection?.activityNo || '');
      return;
    }

    // 只有幣種變更時才重新載入 bonus 清單。
    if (changes['currentCurrency'] && !this.isFirst) {
      this.checkBonus(false);
    }
  }

  ngOnDestroy(): void {
    // 只有到qrCode頁面要保留bonus資料，其餘都銷毀
    if (this.walletService.walletDialogType$.getValue() !== 'address_qr') {
      this.depositBonusDropdownService.clearBonusState();
    }
  }

  /**
   * 獲取存款紅利與整理資料
   * @param currency
   * @param amount
   * @param isInit
   */
  checkBonus(isInit = false) {
    this.bonusLoading = true;
    this.bonusLoadingChange.emit(this.bonusLoading);

    // reset data start
    this.rateVos = null;
    this.selectedBonus = '';
    this.bonusList = [];
    this.depositBonusDropdownService.clearBonusState();
    // reset data end

    // 獲取deposit bonusList
    this.depositBonusDropdownService.getDepositBonus(this.currentCurrency).subscribe(data => {
      if (data) {
        this.bonusLoading = false;
        this.bonusLoadingChange.emit(this.bonusLoading);
        // 舊版裡面還有確認非黏性獎金/歐洲流程/paymentIQ的功能，後續要再添加
        this.bonusList = data.map(item => {
          return {
            ...item,
            label: this.depositBonusDropdownService.prizeAmountTypeTmp(item),
            value: item.bonusActivitiesNo,
            icon: 'fi-gift-filled',
          };
        });
        // 把不選擇紅利的資料push進去
        this.bonusList.push({ value: '', label: 'No Bonus', icon: '' });

        if (this.bonusSelectionFlow === 'transferIntent') {
          this.applyTransferIntentDefaultBonusSelection(isInit);
        } else {
          this.applyLegacyDefaultBonusSelection(isInit);
        }
      }
    });
  }

  /**
   * 選擇紅利
   * 每次更改選擇的紅利都要存到service裡面
   * @param value
   */
  changeBonus(value: string | null) {
    this.updateBonusSelection(value as string);
  }

  /**
   * 不選擇紅利
   */
  noBonusSelected() {
    this.selectedBonus = '';
    this.updateBonusSelection('');
  }

  /**
   * 更新紅利選擇並處理相關邏輯
   * 將重複代碼提取到此方法
   * @param bonusActivityNo 所選紅利活動編號
   */
  updateBonusSelection(bonusActivityNo: string): void {
    // 保存紅利信息到服務
    this.depositBonusDropdownService.saveBonusInfo(bonusActivityNo, this.bonusList);

    if (this.bonusSelectionFlow === 'transferIntent') {
      // Transfer intent flow 的 frontend cache 在 modal close 後仍需保留。
      const uid = this.getCurrentUid();
      if (uid) {
        this.depositBonusDropdownService.saveCachedSelection(uid, this.currentCurrency, bonusActivityNo);
      }
    }

    // 更新當前倍率信息
    this.rateVos = this.bonusList.find(x => x.bonusActivitiesNo === bonusActivityNo)?.rateVos[0] || null;
    // 如果有 rateVos，計算 minDeposit
    if (this.rateVos) {
      const rate = this.allRateBaseUsdt.find(r => r.currency === this.currentCurrency)?.rate || 1;
      this.rateVos.minDeposit = this.rateVos.minDepositUsdt.divide(rate);
    }

    // Web3 wallet / PIQ 保留原本選取後立即儲存 bonus 的行為。
    if (this.bonusSelectionFlow === 'legacyImmediate') {
      this.sendBonusActivity(bonusActivityNo);
    }

    // 發出選擇變更事件
    this.activityNo.emit(bonusActivityNo);

    const selectedBonusItem = this.bonusList.find(item => item.bonusActivitiesNo === bonusActivityNo);

    this.bonusSelectionChange.emit({
      activityNo: bonusActivityNo,
      bonusName: bonusActivityNo ? selectedBonusItem?.bonusActivityName || selectedBonusItem?.label || '' : 'No Bonus',
    });
  }

  private applyLegacyDefaultBonusSelection(isInit: boolean): void {
    if (isInit && !this.bonusId) {
      this.noBonusSelected();
      return;
    }

    this.selectedBonus = this.bonusList.length > 1 ? this.bonusId || this.bonusList[0].bonusActivitiesNo : '';
    this.updateBonusSelection(this.selectedBonus);
  }

  private applyTransferIntentDefaultBonusSelection(isInit: boolean): void {
    const savedBonus = this.syncSavedIntentBonus();

    // 1. backend intent 優先，一旦存在就成為 source of truth
    if (savedBonus) {
      this.selectedBonus = savedBonus.bonusActivitiesNo;
      this.updateBonusSelection(this.selectedBonus);
      return;
    }

    // 2. 沒有 backend intent 時，才使用 frontend cache
    const uid = this.getCurrentUid();
    const cachedBonus = uid
      ? this.depositBonusDropdownService.getCachedSelection(uid, this.currentCurrency)
      : undefined;

    // 要用 !== undefined，因為 '' 代表 No Bonus，也是有效 cache
    if (cachedBonus !== undefined) {
      const isBonusSelectionValid = this.isBonusSelectionValid(cachedBonus);

      if (isBonusSelectionValid) {
        this.selectedBonus = cachedBonus;
        this.updateBonusSelection(this.selectedBonus);
        return;
      }

      this.depositBonusDropdownService.clearCachedSelection(uid, this.currentCurrency);
    }

    // 3. 沒有 backend intent，也沒有 frontend cache，才走原本預設邏輯
    if (isInit && !this.bonusId) {
      this.noBonusSelected();
      return;
    }

    const fallbackBonus = this.getFallbackBonusSelection(isInit);
    this.selectedBonus = fallbackBonus;
    this.updateBonusSelection(this.selectedBonus);
  }

  /**
   * Web3 wallet / PIQ 選擇 bonus 後立即送出原有 mapping API。
   * unknowtmpcode 為後端指定的 No Bonus 參數。
   * @param activityNo
   */
  private sendBonusActivity(activityNo: string): void {
    this.bonusApi
      .onCryptoActivityNo({
        activityNo: activityNo || 'unknowtmpcode',
      })
      .subscribe();
  }

  private isBonusSelectionValid(cachedBonus: string): boolean {
    return cachedBonus === '' || this.bonusList.some(item => item.bonusActivitiesNo === cachedBonus);
  }

  private getFallbackBonusSelection(isInit: boolean): string {
    if (this.bonusId && this.isBonusSelectionValid(this.bonusId)) {
      return this.bonusId;
    }

    if (isInit) return '';

    return this.bonusList.length > 1 ? this.bonusList[0].bonusActivitiesNo : '';
  }

  /**
   * 同步後端目前保存的 crypto deposit bonus intent 給 parent。
   * 回傳 saved bonus item，讓呼叫端可以決定 dropdown 要不要預設選中它。
   * @returns saved bonus item if backend intent exists.
   */
  private syncSavedIntentBonus() {
    const savedBonus = this.bonusList.find(item => item.isCurrentCryptoDepositBonus);

    this.savedIntentBonusChange.emit(
      savedBonus
        ? {
            hasIntent: true,
            activityNo: savedBonus.bonusActivitiesNo,
            bonusName: savedBonus.bonusActivityName || savedBonus.label || '',
          }
        : {
            hasIntent: false,
            activityNo: '',
            bonusName: 'No Bonus',
          },
    );

    return savedBonus;
  }

  private getCurrentUid(): string {
    return this.appService.userInfo$.value?.uid || this.localStorageService.localUserInfo?.uid || '';
  }
}
